/**
 * API lib — Module Admin Import.
 * Consomme les endpoints backend Phase 4.B.1+4.B.2+4.B.4.
 * Phase 4.J.1 · DECISIONS #103
 *
 * Endpoints utilisés :
 *  POST  /imports/exchange-rate
 *  POST  /imports/rawdata              (multipart)
 *  POST  /imports/activity-report      (multipart)
 *  POST  /imports/:batchId/reconcile
 *  GET   /imports/:batchId/reconciliation-report
 *  POST  /imports/:batchId/reconciliation-report/decide
 *  GET   /imports/history
 */

import { urlApi } from '@/lib/urlApi';
import { apiClient, fetchAuthentifie } from '@/lib/apiClient';
import type {
  ExchangeRateResponse,
  UploadResponse,
  ReconciliationReport,
  ImportHistoryResponse,
  CurrentBatchResponse,
} from '../types/import.types';

// ── FX Rate ───────────────────────────────────────────────────────────────────

export interface SetFxRatePayload {
  iso_week: string;
  da_per_usd: number;
  notes?: string;
}

export async function setFxRate(payload: SetFxRatePayload): Promise<ExchangeRateResponse> {
  const res = await apiClient.post<ExchangeRateResponse>('/imports/exchange-rate', payload);
  return res.data;
}

// ── Upload multipart — fetch natif (contourne Axios Content-Type: application/json) ──
// Axios 1.x + header par défaut 'Content-Type: application/json' → 406 sur @fastify/multipart.
// fetch natif : aucun Content-Type posé sur FormData → le navigateur injecte
// automatiquement multipart/form-data; boundary=xxxx. Solution bulletproof.

async function uploadMultipart(endpoint: string, file: File): Promise<UploadResponse> {
  const form = new FormData();
  form.append('file', file);

  // NE PAS setter Content-Type : le browser le fait avec le boundary correct
  // `#1090` — jeton posé et rafraîchi sur 401 par `fetchAuthentifie`.
  const res = await fetchAuthentifie(urlApi(`/api/v1${endpoint}`), {
    method: 'POST',
    body: form,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = body as { error?: { code?: string; message?: string; params?: Record<string, string | number> } };
    // ⚠ `#1089` — les PARAMÈTRES voyagent jusqu'à l'écran : c'est avec eux qu'il compose la
    //   phrase (`libelleErreurEnvoi`). Sans eux, il ne restait que « HTTP 422 ».
    throw {
      status: res.status,
      code: err?.error?.code ?? 'UPLOAD_ERROR',
      message: err?.error?.message ?? `HTTP ${res.status}`,
      params: err?.error?.params,
    };
  }

  return res.json() as Promise<UploadResponse>;
}

export function uploadRawdata(file: File, weekIso?: string): Promise<UploadResponse> {
  const qs = weekIso ? `?week_iso=${encodeURIComponent(weekIso)}` : '';
  return uploadMultipart(`/imports/rawdata${qs}`, file);
}

/** WHOLESALE-IMPORT : upload du 3ème fichier (RAWDATA_WHSL_W*.xlsx) — optionnel. */
export function uploadWholesaleRawdata(file: File, weekIso?: string): Promise<UploadResponse> {
  const qs = weekIso ? `?week_iso=${encodeURIComponent(weekIso)}` : '';
  return uploadMultipart(`/imports/rawdata-wholesale${qs}`, file);
}

export function uploadActivityReport(file: File, weekIso?: string): Promise<UploadResponse> {
  const qs = weekIso ? `?week_iso=${encodeURIComponent(weekIso)}` : '';
  return uploadMultipart(`/imports/activity-report${qs}`, file);
}

// ── Reconciliation ────────────────────────────────────────────────────────────

/**
 * Réponse 202 du POST /imports/:batchId/reconcile (V4.10 ASYNC-RECONCILIATION).
 * Le job tourne en arrière-plan (worker BullMQ `import-reconciliation`),
 * le frontend poll `getReconciliationStatus()` jusqu'à statut terminal
 * (`AWAITING_REVIEW` succès, `FAILED` erreur).
 */
export interface ReconciliationEnqueueResponse {
  job_id:   string;
  batch_id: string;
  status:   'QUEUED';
}

/**
 * Statut courant de la réconciliation (V4.10) — polling fire-and-forget.
 * Statuts terminaux qui stoppent le polling :
 *   AWAITING_REVIEW — succès, report disponible (rediriger vers la page rapport)
 *   FAILED          — échec worker (afficher message + bouton relancer)
 */
export interface ReconciliationStatusResponse {
  batch_id:                     string;
  reconciliation_status:        string;  // QUEUED | RECONCILING | AWAITING_REVIEW | FAILED | ...
  reconciliation_started_at:    string | null;
  reconciliation_completed_at:  string | null;
  report_id:                    string | null;
}

/**
 * V4.10 ASYNC-RECONCILIATION — POST fire-and-forget.
 *
 * Le backend enqueue le job BullMQ et répond 202 immédiat avec { job_id }.
 * Plus de timeout HTTP (le quick fix `timeout: 600_000` V4.04 est retiré).
 * L'appelant doit ensuite poll `getReconciliationStatus()` jusqu'à statut
 * terminal pour récupérer le report ou afficher l'échec.
 */
export async function runReconciliation(batchId: string): Promise<ReconciliationEnqueueResponse> {
  const res = await apiClient.post<ReconciliationEnqueueResponse>(
    `/imports/${batchId}/reconcile`,
  );
  return res.data;
}

/** V4.10 ASYNC-RECONCILIATION — lit le statut courant pour le polling frontend. */
export async function getReconciliationStatus(batchId: string): Promise<ReconciliationStatusResponse> {
  const res = await apiClient.get<ReconciliationStatusResponse>(
    `/imports/${batchId}/reconciliation-status`,
  );
  return res.data;
}

export async function getReconciliationReport(batchId: string): Promise<ReconciliationReport> {
  const res = await apiClient.get<ReconciliationReport>(
    `/imports/${batchId}/reconciliation-report`
  );
  return res.data;
}

export async function refreshReconciliationReport(batchId: string): Promise<ReconciliationReport> {
  const res = await apiClient.post<ReconciliationReport>(
    `/imports/batches/${batchId}/refresh-report`
  );
  return res.data;
}

export interface DecidePayload {
  decision: 'APPROVED' | 'APPROVED_WITH_FORCING' | 'REJECTED';
  decision_notes?: string;
}

export async function decideReconciliation(
  batchId: string,
  payload: DecidePayload
): Promise<ReconciliationReport> {
  const res = await apiClient.post<ReconciliationReport>(
    `/imports/${batchId}/reconciliation-report/decide`,
    payload
  );
  return res.data;
}

export async function finalizeReconciliation(
  batchId: string
): Promise<{ visits_inserted: number; observations_inserted: number }> {
  // FIX-PUBLISH-TIMEOUT : timeout élargi à 600 s (10 min) pour ce seul endpoint.
  // La publication traite ~17 000 lignes staging + observations + trust scores sur Neon
  // free tier — plusieurs minutes sont normales. Le timeout global apiClient (30 s)
  // ne s'applique PAS ici grâce au override par-requête.
  const res = await apiClient.post<{ visits_inserted: number; observations_inserted: number }>(
    `/imports/batches/${batchId}/finalize`,
    undefined,
    { timeout: 600_000 },
  );
  return res.data;
}

// ── FINALISATION-FENETRE-INSERTION-PURGE (option C) — bouton « terminer » ────

export interface TerminerImportResult {
  batch_id:              string;
  reconciliation_status: string;
  changed:               boolean;
}

/**
 * Termine un import resté FINALIZED (bloqué par l'ancienne fenêtre insertion↔purge).
 * Idempotent : rejoue la purge conditionnelle + COMPLETED. Timeout élargi (même
 * charge potentielle que la finalisation).
 */
export async function terminerImport(batchId: string): Promise<TerminerImportResult> {
  const res = await apiClient.post<TerminerImportResult>(
    `/imports/batches/${batchId}/terminer`,
    undefined,
    { timeout: 600_000 },
  );
  return res.data;
}

// ── REPLAY-BOUTON-EXPLICITE (#B4) ────────────────────────────────────────────

export interface ReplayResult {
  triggered:              boolean;
  message:                string;
  batchId:                string;
  weekIso:                string | null;
  rawdataPendingFound:    number;
  rawdataInserted:        number;
  rawdataStillPending:    number;
  activityPendingIgnored: number;
}

/** `A.63` (#543) -- verdict REEL de la relance : le geste est synchrone. */
export interface RefreshAggregatesResult {
  refreshed: boolean;
  duration_ms: number;
  refreshed_objects: string[];
  error_message: string | null;
}

/**
 * `A.63` (#543) -- relance le rafraichissement de la vue lue par le dashboard.
 * ⚠ ~10 s cote serveur ; le delai d attente client est de 30 s (`apiClient`),
 *   la marge est verifiee. Un 409 `REFRESH_ALREADY_RUNNING' signifie qu un
 *   rafraichissement (bouton OU import) tourne deja.
 */
export async function refreshAggregates(): Promise<RefreshAggregatesResult> {
  const res = await apiClient.post<RefreshAggregatesResult>('/imports/aggregates/refresh');
  return res.data;
}

export async function replayPendingRows(batchId: string): Promise<ReplayResult> {
  const res = await apiClient.post<ReplayResult>(`/imports/batches/${batchId}/replay`);
  return res.data;
}

/**
 * Retourne le batch en cours non-terminal pour le bouton Réconciliation TopNav.
 *
 * BUG-RECON-BUTTON-HIDDEN : l'ancienne version ne cherchait que APPROVED +
 * AWAITING_REVIEW — APPROVED_WITH_FORCING et les autres statuts actifs passaient
 * à travers. On utilise désormais getCurrentBatch (NOT IN terminal) pour couvrir
 * tous les cas en un seul appel.
 */
export async function fetchActiveBatch(): Promise<{ id: string; reconciliation_status: string } | null> {
  const batch = await fetchCurrentBatch();
  if (!batch) return null;
  return { id: batch.id, reconciliation_status: batch.reconciliationStatus };
}

// ── Template downloads ────────────────────────────────────────────────────────

async function downloadTemplate(endpoint: string, filename: string): Promise<void> {
  const res = await fetchAuthentifie(urlApi(`/api/v1${endpoint}`));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Délai court : laisser le navigateur initier le téléchargement avant de libérer l'URL
  setTimeout(() => URL.revokeObjectURL(url), 150);
}

export function downloadRawdataTemplate(): Promise<void> {
  return downloadTemplate('/imports/templates/rawdata', 'template_rawdata.xlsx');
}

export function downloadActivityTemplate(): Promise<void> {
  return downloadTemplate('/imports/templates/activity-report', 'template_rapport_activite.xlsx');
}

export function downloadWholesaleTemplate(): Promise<void> {
  return downloadTemplate('/imports/templates/wholesale', 'template_rawdata_wholesale.xlsx');
}

// ── Current batch (HOTFIX-WIZARD-STATE) ──────────────────────────────────────

/**
 * GET /imports/batches/current — `null` = aucun batch en cours : 200 + `null` depuis le `#1089`
 * (la branche 404 ci-dessous reste pour un serveur plus ancien — même sens).
 * Utilisé au mount du wizard pour hydrater l'état.
 */
export async function fetchCurrentBatch(): Promise<CurrentBatchResponse | null> {
  try {
    const res = await apiClient.get<CurrentBatchResponse>('/imports/batches/current');
    return res.data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } }).response?.status;
    if (status === 404) return null;
    throw err;
  }
}

// ── Purge batch staging (FEAT-EMPTY-BATCH) ────────────────────────────────────

export async function purgeBatchStagingApi(
  batchId: string
): Promise<{ purged: boolean; batchId: string }> {
  const res = await apiClient.delete<{ purged: boolean; batchId: string }>(
    `/imports/batches/${batchId}/staging`
  );
  return res.data;
}

// ── Purge all ─────────────────────────────────────────────────────────────────

export interface PurgeAllResult {
  deletedBatches: number;
  deletedStagingRows: number;
}

export async function purgeAllImports(): Promise<PurgeAllResult> {
  const res = await apiClient.delete<PurgeAllResult>('/imports/batches/purge-all');
  return res.data;
}

// ── History ───────────────────────────────────────────────────────────────────

export interface ImportHistoryParams {
  page?: number;
  page_size?: number;
  status?: string;
  week_iso?: string;
}

export async function fetchImportHistory(
  params?: ImportHistoryParams
): Promise<ImportHistoryResponse> {
  const res = await apiClient.get<ImportHistoryResponse>('/imports/history', { params });
  return res.data;
}

// ── Delete batch ──────────────────────────────────────────────────────────────

/**
 * D-DELETE-BATCH-ASYNC — la suppression est un JOB, plus une requête bloquante.
 * Le DELETE synchrone timeoutait à 30 s côté client (~41 000 obs en cascade)
 * pendant que le backend continuait en aveugle. Ici : 202 immédiat + job_id.
 */
export interface DeleteBatchQueued {
  job_id:    string;
  batch_id:  string;
  week_iso?: string;
  status:    'QUEUED';
}

export type DeleteBatchStatus = 'NONE' | 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED';

export interface DeleteBatchStatusResult {
  /** ⚠ `#841` — le code du refus ; le message technique reste au serveur. */
  failure_code?: string | null;
  batch_id: string;
  status:   DeleteBatchStatus;
  /** Raison de l'échec — remontée telle quelle (doctrine #317 : pas de silence). */
  message:  string | null;
  /** Lignes supprimées à cet instant (progression des lots). */
  deleted:  number | null;
}

/** Enfile la suppression d'un batch (SUPER_ADMIN). Réponse 202 immédiate. */
export async function deleteImportBatch(batchId: string): Promise<DeleteBatchQueued> {
  const res = await apiClient.delete<DeleteBatchQueued>(`/imports/batches/${batchId}`);
  return res.data;
}

/**
 * État de la suppression asynchrone — pour le spinner + le message d'échec.
 *
 * ⚠ timeout court VOLONTAIRE (8 s, vs 30 s global). Pendant que le worker
 * supprime (~5 min), chaque poll traverse `requirePermission` qui fait 2
 * requêtes Prisma — or le pool est à `connection_limit=15&pool_timeout=30`.
 * Un poll peut donc attendre une connexion jusqu'à 30 s. Mieux vaut le lâcher
 * vite et re-poller 2 s plus tard que de bloquer sur les 30 s d'axios.
 */
export async function getDeleteBatchStatus(batchId: string): Promise<DeleteBatchStatusResult> {
  const res = await apiClient.get<DeleteBatchStatusResult>(
    `/imports/batches/${batchId}/delete-status`,
    { timeout: 8_000 },
  );
  return res.data;
}
